// pages/KienThuc/index.tsx
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import PageHero from '../../components/common/PageHero'
import { Loader, Pagination } from '../../components/ui'
import { useArticles } from '../../features/articles/hooks/useArticles'
import type { ArticleCategory } from '../../features/articles/types'
import useDocumentTitle from '../../hooks/useDocumentTitle'
import './KienThuc.css'

interface KienThucPageProps {
  category?: ArticleCategory
}

const ITEMS_PER_PAGE = 6

const KienThucPage = ({ category = 'cam-nang' }: KienThucPageProps) => {
  // Fetch 5 trang bài viết (~90 bài viết) để phân trang mượt mà ở frontend
  const { articles, loading, error, currentPage, changePage } = useArticles(category, 5)

  // Cấu hình nội dung hiển thị theo danh mục bài viết
  const isPhongThuy = category === 'phong-thuy'

  useEffect(() => {
    try {
      const path = isPhongThuy ? '/kien-thuc/phong-thuy' : '/kien-thuc/cam-nang-xay-nha'
      const name = isPhongThuy ? 'Phong thủy' : 'Cẩm nang xây nhà'
      sessionStorage.setItem('lastVisitedList', path)
      sessionStorage.setItem('lastVisitedListName', name)
    } catch (e) {
      // ignore
    }
  }, [isPhongThuy])
  
  const heroConfig = {
    tag: isPhongThuy ? 'PHONG THỦY' : 'CẨM NANG',
    title: isPhongThuy ? 'Phong Thủy Xây Dựng' : 'Cẩm Nang Xây Nhà',
    titleHighlight: isPhongThuy ? 'Phong Thủy' : 'Xây Nhà',
    description: isPhongThuy 
      ? 'Tổng hợp kiến thức phong thủy xây dựng nhà ở, cách xem tuổi làm nhà, hướng nhà, hướng bếp giúp gia chủ cát tường, tài lộc.'
      : 'Chia sẻ các kinh nghiệm xây dựng thực tế, quy trình hoàn thiện ngôi nhà, thủ tục pháp lý và phương pháp dự toán chi phí tối ưu.',
    breadcrumbs: [
      { label: 'Trang chủ', path: '/' },
      { label: 'Kiến thức', path: '/kien-thuc' },
      { label: isPhongThuy ? 'Phong thủy' : 'Cẩm nang xây nhà' }
    ]
  }

  useDocumentTitle(`${heroConfig.title} - MAXCONST`)

  // Tính toán phân trang
  const totalCount = articles.length
  const pageCount = Math.ceil(totalCount / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalCount)
  const displayedArticles = articles.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  const handlePageChange = (page: number) => {
    changePage(page)
    // Cuộn mượt lên trên phần danh sách bài viết
    const filterEl = document.getElementById('kien-thuc-content-start')
    if (filterEl) {
      filterEl.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const goToNextPage = () => {
    if (currentPage < pageCount) {
      handlePageChange(currentPage + 1)
    }
  }

  const goToPrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1)
    }
  }

  const getPageNumbers = (): (number | '...')[] => {
    const pages: (number | '...')[] = []
    
    if (pageCount <= 7) {
      for (let i = 1; i <= pageCount; i++) {
        pages.push(i)
      }
      return pages
    }

    pages.push(1)

    const leftBound = Math.max(2, currentPage - 2)
    const rightBound = Math.min(pageCount - 1, currentPage + 2)

    if (leftBound > 2) {
      pages.push('...')
    }

    let start = leftBound
    let end = rightBound
    
    if (currentPage <= 4) {
      start = 2
      end = 5
    } else if (currentPage >= pageCount - 3) {
      start = pageCount - 4
      end = pageCount - 1
    }

    for (let i = start; i <= end; i++) {
      if (i > 1 && i < pageCount) {
        pages.push(i)
      }
    }

    if (end < pageCount - 1) {
      pages.push('...')
    }

    pages.push(pageCount)

    return pages
  }

  return (
    <div className="kien-thuc-page">
      <PageHero
        tag={heroConfig.tag}
        title={heroConfig.title}
        titleHighlight={heroConfig.titleHighlight}
        description={heroConfig.description}
        breadcrumbs={heroConfig.breadcrumbs}
      />

      <div id="kien-thuc-content-start" className="container kien-thuc__content">
        
        {/* Loading state */}
        {loading && (
          <div className="kien-thuc__loading-wrap">
            <Loader text="Đang tải danh sách bài viết..." />
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="kien-thuc__error-wrap">
            <i className="fas fa-exclamation-circle"></i>
            <p>{error}</p>
          </div>
        )}

        {/* Article Grid & Pagination */}
        {!loading && !error && (
          <>
            {displayedArticles.length > 0 ? (
              <>
                <div className="kien-thuc__grid">
                  {displayedArticles.map((article) => (
                    <article key={article.id} className="article-card">
                      <div className="article-card__img-wrapper">
                        <Link to={`/chi-tiet/${article.slug}`}>
                          <img 
                            src={article.image} 
                            alt={article.title} 
                            className="article-card__img" 
                            referrerPolicy="no-referrer"
                          />
                        </Link>
                        <span className="article-card__category">
                          {isPhongThuy ? 'Phong thủy' : 'Cẩm nang'}
                        </span>
                      </div>
                      
                      <div className="article-card__body">
                        <div className="article-card__meta">
                          <span className="article-card__date">
                            <i className="far fa-calendar-alt"></i> {article.date}
                          </span>
                          <span className="article-card__read-time">
                            <i className="far fa-clock"></i> {article.readTime}
                          </span>
                        </div>
                        
                        <h3 className="article-card__title">
                          <Link to={`/chi-tiet/${article.slug}`}>
                            {article.title}
                          </Link>
                        </h3>
                        
                        <p className="article-card__summary">{article.summary}</p>
                        
                        <div className="article-card__footer">
                          <Link 
                            to={`/chi-tiet/${article.slug}`} 
                            className="article-card__link"
                          >
                            Đọc bài viết <i className="fas fa-arrow-right"></i>
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {/* Pagination Controls */}
                {pageCount > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pageCount}
                    totalCount={totalCount}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    getPageNumbers={getPageNumbers}
                    onPageChange={handlePageChange}
                    onPrev={goToPrevPage}
                    onNext={goToNextPage}
                    className="kien-thuc__pagination"
                  />
                )}
              </>
            ) : (
              <div className="kien-thuc__empty">
                <i className="fas fa-folder-open"></i>
                <p>Không tìm thấy bài viết nào trong danh mục này.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default KienThucPage
